import React, { useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  ArrowUpRightIcon,
  CheckIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  RadioIcon,
  RefreshCwIcon,
  SparklesIcon,
  TrashIcon,
} from 'lucide-react'

import { EmptyState, StatusChip } from 'components/display'
import { QueryErrorState } from 'components/feedback'
import { Table } from 'components/display/Table/Table'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { ActionDropdown, ActionDropdownItem } from 'components/input/ActionDropdown'
import { MARKET_SOURCE_HEALTH } from 'constants/statuses'
import {
  useDeleteMarketSource,
  useMarketSourcesQuery,
  usePollMarketSource,
  useUpdateMarketSource,
} from 'hooks/api'
import { datetime, getErrorMessage, timeAgo } from 'utils'
import type { IMarketSource } from 'services/marketSourcesApi'

import { MarketSourceFormModal } from './MarketSourceFormModal'

/**
 * The Sources tab of `/internal/market` — the feed registry behind the corpus (VRG-462).
 *
 * ## The health column is the reason this page exists
 *
 * AC8 requires that a source erroring repeatedly is visible here rather than only in
 * logs, and the failure it guards against is specifically a QUIET one: a feed that
 * has been 404ing for a week produces no items, and "no items" looks identical to
 * "this publication had a slow week" unless something says otherwise. So health is
 * derived from three inputs, not one — whether it is enabled, whether it is
 * currently failing, and whether it has ever actually produced anything.
 */
interface MarketSourcesTabProps {
  /** Owned by the shell so the Add button can live in the tab row's action slot. */
  addOpen: boolean
  onCloseAdd: () => void
}

export const MarketSourcesTab = ({ addOpen, onCloseAdd }: MarketSourcesTabProps) => {
  const theme = useTheme()

  const { data: sources = [], isLoading, isError, error, refetch } = useMarketSourcesQuery()
  const updateSource = useUpdateMarketSource()
  const pollSource = usePollMarketSource()
  const deleteSource = useDeleteMarketSource()

  const [actionError, setActionError] = useState<string | null>(null)
  const [actionNote, setActionNote] = useState<string | null>(null)
  const [editing, setEditing] = useState<IMarketSource | null>(null)

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    setActionNote(null)
    try {
      await fn()
    } catch (e) {
      setActionError(getErrorMessage(e))
    }
  }

  /** See the page docblock — three inputs, because "quiet" is its own state. */
  const healthOf = (row: IMarketSource): keyof typeof MARKET_SOURCE_HEALTH => {
    if (!row.enabled) return 'paused'
    if (row.consecutiveFailures > 0) return 'failing'
    if (row.itemCount === 0) return 'quiet'
    return 'healthy'
  }

  const rowActions = (row: IMarketSource): ActionDropdownItem[] => {
    const pollable = row.kind === 'rss' || row.kind === 'sitemap'
    return [
      {
        // VRG-741 — name, kind, category and the competitor link (the URL is fixed once created).
        label: 'Edit source',
        icon: <PencilIcon strokeWidth={1} />,
        onClick: () => setEditing(row),
      },
      {
        label: 'Poll now',
        icon: <RefreshCwIcon strokeWidth={1} />,
        // RECORD STATE, so hidden rather than disabled (§46): a social source has
        // nothing to poll by construction, and the resolving action is not something
        // an admin grants — it is choosing a different source kind.
        visible: pollable,
        onClick: () =>
          run(async () => {
            const outcome = await pollSource.mutateAsync(row.id)
            setActionNote(
              `${row.name}: ${outcome.itemsIngested} new, ${outcome.itemsDuplicate} already known, ${outcome.itemsRejected} rejected.${
                outcome.itemsIngested ? ' New items were checked for content ideas (GTM → Articles → Ideas).' : ''
              }`,
            )
          }),
      },
      {
        /*
          THE COST SWITCH. Extraction is a billed model call whose price tracks how many
          figures an article holds — measured 0.2c to 3.7c across six real runs — and
          every proposal it makes needs a human to approve or reject it. Turning this on
          for a feed that publishes 25 price-dense listicles a day buys both a bill and an
          unreviewable queue, so it is a per-source decision made by someone who knows
          what that source publishes.

          HIDDEN on a source that is never polled (§46: record state hides): a social or
          "other" source has no ingest to hang extraction off, so the toggle would offer
          something that cannot happen.
        */
        label: row.autoExtract ? 'Turn off auto-extract' : 'Auto-extract new items',
        icon: <SparklesIcon strokeWidth={1} />,
        visible: row.kind === 'rss' || row.kind === 'sitemap',
        onClick: () =>
          run(async () => {
            await updateSource.mutateAsync({id: row.id, autoExtract: !row.autoExtract})
            setActionNote(
              row.autoExtract
                ? `${row.name}: new items will no longer be extracted automatically.`
                : `${row.name}: new items will be extracted on arrival. Proposals land on the Stats tab for review.`,
            )
          }),
        divider: true,
      },
      {
        label: row.enabled ? 'Pause polling' : 'Resume polling',
        icon: row.enabled ? <PauseIcon strokeWidth={1} /> : <PlayIcon strokeWidth={1} />,
        onClick: () => run(() => updateSource.mutateAsync({ id: row.id, enabled: !row.enabled })),
        divider: true,
      },
      {
        label: 'Delete source',
        icon: <TrashIcon strokeWidth={1} />,
        // Destructive row in a transient menu → always red (§32). Trash rather than X
        // because the record really is destroyed (§38) — though its ITEMS survive,
        // which is why pausing is offered first and this sits after a divider.
        destructive: true,
        onClick: () => run(() => deleteSource.mutateAsync(row.id)),
      },
    ]
  }

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Source',
        type: 'string',
        /*
          A WIDTH, not `flex: 2`. Flexed, this column took 894px of a 2008px grid — a
          publication name is rarely 20 characters — and shoved Kind/Health/Items/Every
          out past the middle of the screen, so the row read as two clusters separated by
          a void rather than as a table. Companies, the reference internal list, gives its
          name column 298.

          The slack belongs at the END, which is what `lastError`'s `flex: 1` already does
          with it: a column that genuinely varies in length, absorbing the remainder at the
          right edge where empty space is unremarkable.
        */
        width: 280,
        sortable: true,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Stack
            component="a"
            href={row.url}
            target="_blank"
            rel="noreferrer"
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'inherit', textDecoration: 'none', minWidth: 0 }}
          >
            <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>
              {row.name}
            </Typography>
            <ArrowUpRightIcon strokeWidth={1} size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
          </Stack>
        ),
      },
      {
        // VRG-741 — the competitor this feed belongs to (its profile lives on GTM → SEO).
        field: 'competitorName',
        headerName: 'Competitor',
        type: 'string',
        width: 170,
        sortable: true,
        valueGetter: (_v: unknown, row: IMarketSource) => (row ? row.competitorName ?? row.competitorDomain ?? '' : ''),
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color={row.competitorDomain ? 'text.primary' : 'text.secondary'} noWrap>
            {row.competitorDomain ? row.competitorName ?? row.competitorDomain : '—'}
          </Typography>
        ),
      },
      {
        // VRG-741 — "Found automatically": added by the competitor feed finder, not by a person.
        field: 'origin',
        headerName: 'Added',
        renderHeader: headerWithTerm('Added', 'competitor-feed-finder'),
        type: 'string',
        width: 190,
        sortable: true,
        valueGetter: (_v: unknown, row: IMarketSource) =>
          row ? (row.origin === 'auto_discovered' ? 'Found automatically' : 'By hand') : '',
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography
            variant="body3"
            color="text.secondary"
            noWrap
            title={row.includePaths?.length ? `Keeps only ${row.includePaths.join(', ')}` : undefined}
          >
            {row.origin === 'auto_discovered' ? 'Found automatically' : 'By hand'}
            {row.includePaths?.length ? ` · ${row.includePaths.join(', ')}` : ''}
          </Typography>
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        type: 'string',
        width: 130,
        sortable: true,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.category === 'regulatory' ? 'Regulatory' : 'Market'}
          </Typography>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        type: 'string',
        width: 110,
        sortable: true,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.kind}
          </Typography>
        ),
      },
      {
        field: 'health',
        headerName: 'Health',
        type: 'string',
        // 140, not 120: `headerWithTerm` adds a 14px glyph plus a 4px gap, and §49 sizes
        // a column by its HEADER. Measured after the change, not estimated.
        width: 140,
        sortable: false,
        // The four states are not self-evident from the word, and "Quiet" — the one that
        // matters most — is meaningless without its explanation. Definition lives in the
        // glossary so the tooltip, the glossary page and Ask all read one sentence (§44).
        renderHeader: headerWithTerm('Health', 'source-health'),
        renderCell: ({ row }: { row: IMarketSource }) => (
          <StatusChip statuses={MARKET_SOURCE_HEALTH} status={healthOf(row)} size="small" />
        ),
      },
      {
        // Visible in the table, not just buried in a kebab — this is the switch that
        // decides whether a source costs money per item, so which sources have it on
        // should be answerable by scanning the list.
        field: 'autoExtract',
        headerName: 'Auto-extract',
        type: 'boolean',
        // Sized for the HEADER, not the values (§49).
        width: 150,
        sortable: false,
        renderCell: ({ row }: { row: IMarketSource }) =>
          row.autoExtract ? (
            <CheckIcon strokeWidth={1} size={18} color={theme.palette.success.main} />
          ) : (
            <Typography variant="body3" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'itemCount',
        headerName: 'Items',
        type: 'number',
        // Sized for the HEADER, not the values (§49).
        width: 110,
        sortable: false,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color="text.secondary">
            {row.itemCount}
          </Typography>
        ),
      },
      {
        field: 'cadenceMinutes',
        headerName: 'Every',
        type: 'number',
        width: 110,
        sortable: false,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.cadenceMinutes >= 60
              ? `${Math.round(row.cadenceMinutes / 60)}h`
              : `${row.cadenceMinutes}m`}
          </Typography>
        ),
      },
      {
        field: 'lastPolledAt',
        headerName: 'Last polled',
        type: 'string',
        width: 150,
        sortable: false,
        renderCell: ({ row }: { row: IMarketSource }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.lastPolledAt ? timeAgo(row.lastPolledAt) : 'Never'}
          </Typography>
        ),
      },
      {
        field: 'lastError',
        headerName: 'Last error',
        type: 'string',
        flex: 1,
        minWidth: 200,
        sortable: false,
        // The actual AC8 payload. Truncated to one line with the full text and
        // timestamp on hover — a staffer scanning the table needs to see THAT it is
        // failing; the detail is one hover away rather than wrapping every row.
        renderCell: ({ row }: { row: IMarketSource }) =>
          row.lastError ? (
            <Typography
              variant="body3"
              color="error.main"
              noWrap
              title={`${row.lastError}${row.lastErrorAt ? ` (${datetime(row.lastErrorAt)})` : ''}`}
            >
              {row.consecutiveFailures > 1 ? `×${row.consecutiveFailures} ` : ''}
              {row.lastError}
            </Typography>
          ) : (
            <Typography variant="body3" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: IMarketSource }) => <ActionDropdown items={rowActions(row)} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, sources],
  )

  return (
    <Box>
      {actionError && (
        <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.5, mb: 3 }}>
          {actionError}
        </Typography>
      )}
      {actionNote && (
        <Typography variant="body3" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5, mb: 3 }}>
          {actionNote}
        </Typography>
      )}

      {isError ? (
        <QueryErrorState
          error={error}
          subject="market sources"
          permissionLabel="View the market-data corpus"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          loading={isLoading}
          columns={columns}
          rows={sources}
          getRowId={(row: IMarketSource) => row.id}
          fillBottom
          emptyState={
            <EmptyState
              icon={<RadioIcon strokeWidth={1} />}
              title="No sources registered"
              text="Register an RSS or sitemap feed and the poller will file new content into the Content tab for review. Social platforms can't be polled — paste those in by hand."
            />
          }
        />
      )}

      <MarketSourceFormModal open={addOpen} onClose={onCloseAdd} />
      <MarketSourceFormModal open={Boolean(editing)} source={editing} onClose={() => setEditing(null)} />
    </Box>
  )
}
